// App Settings Section - placeholder layout modeled after Account masonry cards
import { MainLayout } from '../../components/Layout';
import { LoadingPanel } from '../../components';
import { useManager, logger } from '../../core';
import { useAuthRequired } from '../../contexts/AuthContext';
import { AppSettingsManager } from './managers/app-settings-manager';
import { determineAppAccess, canShowWorkspaceButton } from '../../core/utils/appAccessUtils';
import { settingsUtils } from '../../core/utils';
import {
  OverviewCard,
  ShareCard,
  AccessCard,
  ConfigurationCard,
  FilePickerModal,
  DangerZoneCard,
  AdminInsightsCard
} from './components';
import { RemixModal } from '../../components';
import './AppSettingsSection.css';
import { useParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { RevoltModal } from '../../components/RevoltModal';
import { RevoltInput } from '../../components/RevoltInput';
import { RevoltButton } from '../../components/RevoltButton';
import { RevoltSegmentedControl } from '../../components';
import { navigationManager } from '../../managers/navigation-manager';

export function AppSettingsSection() {
  const { manager, isInitialized } = useManager(AppSettingsManager);
  const { appId } = useParams();
  const [appState, setAppState] = useState({
    loading: true,
    app: null,
    membership: null,
    members: [],
    isAuthorized: false,
    isOwner: false,
    isAdmin: false,
    isMember: false,
    error: null
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ percent: 0, message: null });
  const [deleteInput, setDeleteInput] = useState('');
  const [entryFilePath, setEntryFilePath] = useState('');
  const [showFilePickerModal, setShowFilePickerModal] = useState(false);
  const [filePickerSaveError, setFilePickerSaveError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [transferringMembers, setTransferringMembers] = useState(new Set());
  const [showRemixModal, setShowRemixModal] = useState(false);
  const [remixModalProject, setRemixModalProject] = useState(null);
  const [remixModalLoading, setRemixModalLoading] = useState(false);
  const [detailedViewInfoEnabled, setDetailedViewInfoEnabled] = useState(false);

  const handleClearFilePickerSaveError = useCallback(() => setFilePickerSaveError(null), []);

  // Load evaluated settings for developer features
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await settingsUtils.getEvaluatedSettings();
        setDetailedViewInfoEnabled(settings.detailedViewInfo);
      } catch (error) {
        console.error('Failed to load settings:', error);
        setDetailedViewInfoEnabled(false);
      }
    };

    loadSettings();
  }, []);

  // Require authentication to view settings
  const { user } = useAuthRequired({
    // App settings are accessible to all authenticated users
    payload: {
      action: 'viewAppSettings',
      section: 'app-settings',
      timestamp: Date.now()
    }
  });

  // Load app data for this appId and user
  useEffect(() => {
    const load = async () => {
      if (!isInitialized || !user) return;
      const currentUserId = user._id || user.id || user.userId;
      setAppState((prev) => ({ ...prev, loading: true, error: null }));
      setLoadingProgress({ percent: 0, message: null });
      const result = await manager.loadApp(appId, currentUserId, user, {
        onProgress: ({ percent, message }) => setLoadingProgress({ percent, message }),
      });
      const app = result?.app || null;
      const isPublicViewer = !!result?.isPublicViewer;

      // When load succeeded, show 100% and "Ready" briefly so the user sees the bar complete
      if (app && !result?.error) {
        setLoadingProgress({ percent: 100, message: "Ready" });
        await new Promise((r) => setTimeout(r, 350));
      }

      setAppState({
        loading: false,
        app: app,
        membership: result?.membership || null,
        members: result?.members || [],
        isAuthorized: !!result?.isAuthorized,
        isOwner: !!result?.isOwner,
        isAdmin: !!result?.isAdmin,
        isMember: !!result?.isMember,
        isSiteAdmin: !!result?.isSiteAdmin,
        error: result?.error || null
      });

      // Increment view counter for public viewers accessing settings
      if (app && !result?.error && isPublicViewer) {
        // Fire and forget - don't block UI on view increment
        fetch(`/api/apps/${appId}/view`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ mode: 'settings' })
        }).catch(error => {
          console.warn('Failed to increment settings view counter:', error);
        });
      }
      // Load entry file path from app settings
      if (app?.settings?.entryFilePath !== undefined) {
        setEntryFilePath(app.settings.entryFilePath || '');
      } else {
        setEntryFilePath('');
      }
      setPage(1);
    };
    load();
  }, [appId, isInitialized, user, manager]);

  const { isOwner, isAdmin, isSiteAdmin } = appState;
  const canManage = isOwner || isAdmin || isSiteAdmin;

  // Sharing (playspace/iframe publish) is browser apps only; local and other app types have no sharing tab
  const showSharingTab = String(appState.app?.appType || '')
    .toLowerCase()
    .trim() === 'browser';

  // Access (members / invites) is hidden for on-disk local apps; all other app types keep it
  const showAccessTab = String(appState.app?.appType || '')
    .toLowerCase()
    .trim() !== 'local';

  useEffect(() => {
    if (!canManage) {
      const memberAllowedTabs = showSharingTab
        ? activeTab === 'overview' || activeTab === 'sharing'
        : activeTab === 'overview';
      if (!memberAllowedTabs) {
        setActiveTab('overview');
      }
    } else if (canManage && !isOwner && activeTab === 'danger') {
      setActiveTab('overview');
    }
  }, [canManage, isOwner, activeTab, showSharingTab]);

  useEffect(() => {
    if (!showSharingTab && activeTab === 'sharing') {
      setActiveTab('overview');
    }
  }, [showSharingTab, activeTab]);

  useEffect(() => {
    if (!showAccessTab && activeTab === 'access') {
      setActiveTab('overview');
    }
  }, [showAccessTab, activeTab]);

  useEffect(() => {
    if (!isSiteAdmin && activeTab === 'admin') {
      setActiveTab('overview');
    }
  }, [isSiteAdmin, activeTab]);

  if (!isInitialized || appState.loading) {
    return (
      <MainLayout contentClassName="main-content--app-settings">
        <div className="loading-container">
          <LoadingPanel
            title="Loading App Settings"
            appId={appId}
            subtitle="Preparing settings and configuration..."
            progress={loadingProgress.percent}
            statusMessage={loadingProgress.message}
          />
        </div>
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout contentClassName="main-content--app-settings">
        <div className="loading-container">
          <LoadingPanel
            title="Authenticating"
            subtitle="Please wait while we verify your credentials..."
            showAppName={false}
          />
        </div>
      </MainLayout>
    );
  }

  if (appState.error === 'forbidden' || !appState.isAuthorized) {
    return (
      <MainLayout contentClassName="main-content--app-settings">
        <div className="loading-container">
          <LoadingPanel
            title="Access Denied"
            appId={appId}
            subtitle="Only owners and admins can view app settings."
          />
        </div>
      </MainLayout>
    );
  }

  if (appState.error && appState.error !== 'forbidden') {
    return (
      <MainLayout contentClassName="main-content--app-settings">
        <div className="loading-container">
          <LoadingPanel
            title="Unable to Load Settings"
            appId={appId}
            subtitle="An error occurred while loading app settings."
          />
        </div>
      </MainLayout>
    );
  }

  const { app, members } = appState;
  const totalPages = Math.max(1, Math.ceil((members?.length || 0) / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pagedMembers = (members || []).slice(startIndex, startIndex + pageSize);

  const refreshMembers = async () => {
    if (!app) return;
    const currentUserId = user._id || user.id || user.userId;

    // Fetch current user's membership to recalculate roles
    const membershipRes = await fetch(
      `/api/app-memberships/by-app?appId=${encodeURIComponent(appId)}&userId=${encodeURIComponent(currentUserId)}`,
      { credentials: 'include' }
    );
    const membershipData = membershipRes.ok ? await membershipRes.json() : null;
    let membership = membershipData?.membership || null;

    // Fetch all members
    const allRes = await fetch(
      `/api/app-memberships/by-app/all?appId=${encodeURIComponent(appId)}`,
      { credentials: 'include' }
    );
    let members = [];
    if (allRes.ok) {
      const allData = await allRes.json();
      members = allData?.memberships || [];
    }

    // Recalculate access/roles using the updated membership
    const access = determineAppAccess(
      app,
      user || { _id: currentUserId, id: currentUserId, userId: currentUserId },
      membership
    );

    // For public viewers, create a virtual membership
    if (access.isPublicViewer) {
      membership = { role: 'member', isPublicView: true };
    }

    // Update state with new membership, members, and recalculated roles
    setAppState((prev) => ({
      ...prev,
      membership,
      members,
      isOwner: access.isOwner,
      isAdmin: access.isAdmin,
      isMember: access.isMember,
      isSiteAdmin: access.isSiteAdmin,
    }));

    setPage(1);
  };

  const handleRoleChange = async (member, role) => {
    if (!app || !member?.userId) return;
    await manager.updateMemberRole(app.id, member.userId, role);
    await refreshMembers();
  };

  const handleRename = async (newName) => {
    if (!app) return;
    const updated = await manager.renameApp(app.id, newName);
    if (updated) {
      setAppState((prev) => ({
        ...prev,
        app: {
          ...prev.app,
          ...updated,
          name: updated.name || newName
        }
      }));
    }
  };

  const handleDescriptionChange = async (description) => {
    if (!app) return;
    const updated = await manager.updateAppDescription(app.id, description);
    if (updated) {
      setAppState((prev) => ({
        ...prev,
        app: {
          ...prev.app,
          ...updated,
          description: updated.description ?? description
        }
      }));
    }
  };

  const handleSaveEntryFilePath = async (newEntryFilePath) => {
    if (!app?.id) return;
    const response = await fetch(`/api/apps/${app.id}/settings/entryFilePath`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ value: newEntryFilePath || null }),
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to save entry file path');
    }
    setEntryFilePath(newEntryFilePath);
    setAppState((prev) => ({
      ...prev,
      app: {
        ...prev.app,
        settings: {
          ...prev.app?.settings,
          entryFilePath: newEntryFilePath,
        },
      },
    }));
  };

  const handleSaveTags = async (tags) => {
    if (!app?.id) return;
    const response = await fetch(`/api/apps/${app.id}/tags`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ tags: Array.isArray(tags) ? tags : [] }),
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to save tags');
    }
    setAppState((prev) => ({
      ...prev,
      app: {
        ...prev.app,
        tags: data.app?.tags || tags,
      },
    }));
  };

  const handleOpenWorkspace = async () => {

    await manager.openWorkspace(appId);
  };

  const handleRemixApp = () => {
    setRemixModalProject(app);
    setShowRemixModal(true);
  };

  const handleConfirmRemix = async (project) => {
    setRemixModalLoading(true);

    try {
      const response = await fetch('/api/apps/remix', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sourceAppId: project._id || project.id,
          name: `${project.name} (Remix)`,
          description: project.description || '',
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowRemixModal(false);
        setRemixModalProject(null);
        setRemixModalLoading(false);

        // Navigate to the workspace (same as workspace button)
        await manager.openWorkspace(result.app._id);
      } else {
        alert(`Failed to remix app: ${result.error}`);
        setRemixModalLoading(false);
      }
    } catch (error) {
      alert(`Failed to remix app: ${error.message}`);
      setRemixModalLoading(false);
    }
  };

  const handleDeleteApp = async () => {
    if (!app) return;
    const ok = await manager.deleteApp(app.id);
    if (ok) {
      await navigationManager.navigateTo("app");
    }
  };

  const handleRemoveMember = async (member) => {
    if (!app || !member?.userId) return;

    const currentUserId = user._id || user.id || user.userId;
    const isSelfRemoval = String(member.userId) === String(currentUserId);
    const isSiteAdmin = appState.membership?.role === 'site_admin';

    await manager.removeMember(app.id, member.userId);

    // If site admin is removing themselves, navigate away to prevent auto-recreation of membership
    if (isSelfRemoval && isSiteAdmin) {
      await navigationManager.navigateTo("app");
    } else {
      await refreshMembers();
    }
  };

  const handleTogglePublic = async () => {
    if (!app?.id) return;
    const nextValue = !app.isPublic;
    const updated = await manager.togglePublic(app.id, nextValue);
    if (updated) {
      setAppState((prev) => ({
        ...prev,
        app: {
          ...prev.app,
          ...updated,
        },
      }));
    }
  };

  const handleToggleRemixing = async (optimisticValue) => {
    if (!app?.id) return;

    try {
      const updated = await manager.toggleRemixing(app.id, optimisticValue);
      if (updated) {
        setAppState((prev) => ({
          ...prev,
          app: {
            ...prev.app,
            ...updated,
          },
        }));
      }
    } catch (error) {
      logger.error({
        tags: "section|app-settings|remixing",
        color1: "red"
      }, "Failed to update remixing setting", error);
      // TODO: Show error toast/notification to user
      throw error; // Re-throw so ShareCard can handle reversion
    }
  };

  const handleTogglePublish = async (nextValue) => {
    if (!app?.id) return;

    try {
      const updated = await manager.togglePublish(app.id, nextValue);
      if (updated) {
        setAppState((prev) => ({
          ...prev,
          app: {
            ...prev.app,
            ...updated,
          },
        }));
      }
    } catch (error) {
      logger.error({
        tags: "section|app-settings|publish",
        color1: "red"
      }, "Failed to update publish setting", error);
      throw error;
    }
  };

  const sharingTabOption = { value: 'sharing', label: 'Sharing' };
  const accessTabOption = { value: 'access', label: 'Access' };
  const adminTabOption = { value: 'admin', label: 'Admin' };
  const settingsTabs = isOwner
    ? [
        { value: 'overview', label: 'Overview' },
        ...(showSharingTab ? [sharingTabOption] : []),
        { value: 'configuration', label: 'Configuration' },
        ...(showAccessTab ? [accessTabOption] : []),
        ...(isSiteAdmin ? [adminTabOption] : []),
        { value: 'danger', label: 'Danger' },
      ]
    : canManage
    ? [
        { value: 'overview', label: 'Overview' },
        ...(showSharingTab ? [sharingTabOption] : []),
        { value: 'configuration', label: 'Configuration' },
        ...(showAccessTab ? [accessTabOption] : []),
        ...(isSiteAdmin ? [adminTabOption] : []),
      ]
    : [
        { value: 'overview', label: 'Overview' },
        ...(showSharingTab ? [sharingTabOption] : []),
      ];

  return (
    <MainLayout contentClassName="main-content--app-settings">
      <div className="account-container app-settings-container">
        <div className="account-content app-settings-content">
          <h1 className="account-title">App Settings</h1>

          <div className="app-settings-tabs">
            <RevoltSegmentedControl
              options={settingsTabs}
              value={activeTab}
              onChange={setActiveTab}
              size="default"
              equalWidthPadding={4}
            />
          </div>

          <div className="app-settings-tab-content">
            {activeTab === 'overview' && (
              <div className="app-settings-grid">
                <div className="app-settings-row">
                  <div className="app-settings-panel is-wide">
                    <OverviewCard
                      app={app}
                      canEdit={isOwner || isAdmin}
                      canShowWorkspace={canShowWorkspaceButton({ isOwner, isAdmin, isSiteAdmin })}
                      isOwner={isOwner}
                      isAdmin={isAdmin}
                      onRename={handleRename}
                      onDescriptionChange={handleDescriptionChange}
                      onOpenWorkspace={handleOpenWorkspace}
                      onOpenPlayspace={async () => await manager.openPlayspace(app.id)}
                      onRemix={handleRemixApp}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'sharing' && (
              <div className="app-settings-grid">
                <div className="app-settings-row">
                  <div className="app-settings-panel is-wide">
                    <ShareCard
                      app={app}
                      canManage={isOwner || isAdmin}
                      isMember={appState.isMember}
                      isPublicViewer={appState.isPublicViewer}
                      detailedViewInfoEnabled={detailedViewInfoEnabled}
                      onTogglePublic={handleTogglePublic}
                      onToggleRemixing={handleToggleRemixing}
                      onTogglePublish={handleTogglePublish}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'access' && showAccessTab && (
              <div className="app-settings-grid">
                <div className="app-settings-row">
                  <div className="app-settings-panel is-wide">
                    <AccessCard
                      canManage={isOwner || isAdmin}
                      isOwner={isOwner}
                      members={pagedMembers}
                      page={currentPage}
                      pageSize={pageSize}
                      totalPages={totalPages}
                      onPageChange={setPage}
                      onPageSizeChange={(size) => {
                        setPageSize(size);
                        setPage(1);
                      }}
                      onRefresh={refreshMembers}
                      onInvite={async ({ email, role }) => {
                        if (!app) return;
                        await manager.inviteMember(app.id, email, role);
                        await refreshMembers();
                      }}
                      onRoleChange={handleRoleChange}
                      onRemove={handleRemoveMember}
                      onTransferOwnership={async (member) => {
                        if (!app) return;

                        // Add member to transferring set
                        setTransferringMembers(prev => new Set([...prev, member.userId]));

                        try {
                          const success = await manager.transferOwnership(app.id, member.userId);
                          if (success) {
                            // Full page reload to ensure UI state consistency after ownership transfer
                            window.location.reload();
                          }
                        } finally {
                          // Remove member from transferring set (in case of error)
                          setTransferringMembers(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(member.userId);
                            return newSet;
                          });
                        }
                      }}
                      transferringMembers={transferringMembers}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'configuration' && (
              <div className="app-settings-grid">
                <div className="app-settings-row">
                  <div className="app-settings-panel is-wide">
                    <ConfigurationCard
                      app={app}
                      canEdit={isOwner || isAdmin}
                      entryFilePath={entryFilePath}
                      filePickerSaveError={filePickerSaveError}
                      onClearFilePickerSaveError={handleClearFilePickerSaveError}
                      onOpenFilePicker={() => {
                        setShowFilePickerModal(true);
                      }}
                      onSaveEntryFilePath={handleSaveEntryFilePath}
                      onSaveTags={handleSaveTags}
                      onSaveAppColor={async (newAppColor) => {
                        if (!app?.id) return;
                        try {
                          const response = await fetch(`/api/apps/${app.id}/settings/appColor`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            credentials: 'include',
                            body: JSON.stringify({ value: newAppColor }),
                          });

                          const data = await response.json();
                          if (!response.ok || !data?.success) {
                            throw new Error(data?.error || 'Failed to save app color');
                          }

                          setAppState((prev) => ({
                            ...prev,
                            app: {
                              ...prev.app,
                              settings: {
                                ...prev.app?.settings,
                                appColor: newAppColor,
                              },
                            },
                          }));
                        } catch (error) {
                          console.error('Failed to save app color:', error);
                          throw error;
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'danger' && (
              <div className="app-settings-grid">
                <div className="app-settings-row">
                  <div className="app-settings-panel is-wide">
                    <DangerZoneCard
                      app={app}
                      isOwner={isOwner}
                      onDelete={() => setShowDeleteModal(true)}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'admin' && isSiteAdmin && (
              <div className="app-settings-grid">
                <div className="app-settings-row">
                  <div className="app-settings-panel is-wide">
                    <AdminInsightsCard app={app} manager={manager} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <FilePickerModal
        isOpen={showFilePickerModal}
        onClose={() => setShowFilePickerModal(false)}
        appId={app?.id}
        onSelectFile={async (filePath) => {
          setShowFilePickerModal(false);
          setEntryFilePath(filePath);
          try {
            await handleSaveEntryFilePath(filePath);
          } catch (error) {
            console.error('Failed to save entry file path from picker:', error);
            setFilePickerSaveError(error?.message || 'Failed to save entry file path.');
          }
        }}
      />

      {showDeleteModal && (
        <RevoltModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete App"
        >
          <div className="delete-modal-body">
            <p className="delete-warning">
              This will permanently delete the app and its data. This action cannot be undone.
            </p>
            <div className="delete-summary">
              <div><strong>Name:</strong> {app?.name}</div>
              <div><strong>Created:</strong> {app?.createdAtFormatted || app?.createdAt}</div>
              <div><strong>Template:</strong> {app?.template} ({app?.appType})</div>
            </div>
            <p className="delete-instructions">
              Type "{app?.name}" to confirm your action.
            </p>
            <div className="delete-input-row">
              <RevoltInput
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder="Type the app name to confirm"
              />
              <RevoltButton
                variant="ghost"
                onClick={() => navigator?.clipboard?.writeText(app?.name || '')}
              >
                Copy Name
              </RevoltButton>
            </div>
            <div className="delete-actions">
              <RevoltButton
                variant="ghost"
                onClick={() => { setShowDeleteModal(false); setDeleteInput(''); }}
              >
                Cancel
              </RevoltButton>
              <RevoltButton
                variant="danger"
                disabled={(deleteInput || '').trim() !== (app?.name || '')}
                onClick={async () => {
                  await handleDeleteApp();
                  setDeleteInput('');
                  setShowDeleteModal(false);
                }}
              >
                Delete
              </RevoltButton>
            </div>
          </div>
        </RevoltModal>
      )}

      <RemixModal
        isOpen={showRemixModal}
        onClose={() => setShowRemixModal(false)}
        onConfirmRemix={handleConfirmRemix}
        project={remixModalProject}
        isLoading={remixModalLoading}
      />
    </MainLayout>
  );
}

